import { Component } from '@theme/component';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { validateVariantSelection } from './variant-selection-validation-v3.js';

/**
 * Consent modal component — intercepts add-to-cart when product has 項目選択肢
 * (product metafield custom.consent_options → metaobject `consent_options`).
 *
 * 各項目は表示タイプ（ラジオボタン / セレクトボックス / テキストフィールド）に応じて
 * ラジオ・select・text input のいずれかで描画される（`snippets/consent-modal.liquid`）。
 * 全項目に回答するまで送信できず、回答は line item property
 * （キー = 項目名 / 値 = 選択された選択肢）としてカートに入る。
 */
class ConsentModalComponent extends Component {
  requiredRefs = ['dialog'];

  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#interceptAddToCart();
    this.#setupFieldListeners();
    this.#setupQuantityControls();

    // #267: モジュールの実行前に「カートに入れる」が押されていた場合、
    // インラインのガードが `data-pending-open` を立てているのでここで開く。
    if (this.dataset.pendingOpen === 'true') {
      delete this.dataset.pendingOpen;
      this.openWithForm();
    }
  }

  /**
   * #267: ページ内フォームの数量を引き継いでモーダルを開く。
   * インラインのガード（`snippets/consent-modal.liquid`）からも呼ばれる。
   */
  openWithForm() {
    const productForm = this.#getProductForm();

    if (!productForm) return;
    if (!validateVariantSelection(productForm)) return;

    const form = productForm.querySelector(
      'form[data-type="add-to-cart-form"]'
    );

    if (!form) return;

    this.#syncQuantityFromForm(form);
    this.showDialog();
  }


  /**
   * 同じセクション内にある、この商品の product form を返す。
   * #267: セクションに複数の product-form-component があるケースで別商品の
   * フォームを掴まないよう、product id で絞る（見つからなければ先頭にフォールバック）。
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const section = this.closest('.shopify-section');
    if (!section) return null;

    const productId = this.dataset.productId;
    return /** @type {HTMLElement | null} */ (
      (productId && section.querySelector(`product-form-component[data-product-id="${productId}"]`)) ||
      section.querySelector('product-form-component')
    );
  }

  /**
   * Intercept the product form's add-to-cart flow.
   * Uses data-puppet to suppress the button animation, and patches handleSubmit
   * on the ProductFormComponent instance to open the consent modal instead.
   *
   * インラインのガードが submit を止めるので通常はここまで来ないが、
   * ガードが無い経路（テーマの JS が直接 handleSubmit を呼ぶ等）の保険として残す。
   */
  #interceptAddToCart() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    // Suppress the fly-to-cart / checkmark animation on the original button
    const addToCartContainer = productForm.querySelector('add-to-cart-component');
    if (addToCartContainer) {
      const btn = addToCartContainer.querySelector('[ref="addToCartButton"]');
      if (btn) btn.dataset.puppet = 'true';
    }

    // Monkey-patch handleSubmit so the theme's own delegated on:submit calls our code
    productForm.handleSubmit = (event) => {
      event.preventDefault();
      this.openWithForm();
    };
  }

  /**
   * Sync quantity from the product page form to the modal.
   */
  #syncQuantityFromForm(form) {
    const pageQtyInput = form.querySelector('input[name="quantity"]');
    const modalQtyInput = this.refs.quantityInput;
    if (pageQtyInput && modalQtyInput) {
      modalQtyInput.value = pageQtyInput.value || '1';
    }
  }

  /**
   * 各項目の入力を監視して送信ボタンの活性を切り替える。
   * ラジオ / select は change、テキストは input で拾う。
   * 項目ごとではなくコンポーネントで委譲して受ける（項目数は商品ごとに違い、
   * 1〜7件と幅があるうえ、後から DOM が差し替わっても効き続ける）。
   */
  #setupFieldListeners() {
    this.addEventListener('change', () => this.#validateConsent());
    this.addEventListener('input', () => this.#validateConsent());

    const submitBtn = this.refs.submitButton;
    if (submitBtn) {
      submitBtn.addEventListener('click', () => this.#handleSubmit());
    }
  }

  /**
   * 了承項目の要素一覧。
   * @returns {HTMLElement[]}
   */
  get #items() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-consent-item]')));
  }

  /**
   * 1項目の回答を返す（未回答なら空文字）。表示タイプごとに入力要素が違うので
   * 「ラジオがあるか」を先に見てから select / text を探す。
   * @param {HTMLElement} item
   * @returns {string}
   */
  #getAnswer(item) {
    const radios = item.querySelectorAll('input[type="radio"]');
    if (radios.length > 0) {
      const checked = /** @type {HTMLInputElement | null} */ (
        item.querySelector('input[type="radio"]:checked')
      );
      return checked ? checked.value : '';
    }

    const field = /** @type {HTMLSelectElement | HTMLInputElement | null} */ (
      item.querySelector('select, input[type="text"], textarea')
    );
    return field ? field.value.trim() : '';
  }

  /**
   * Setup quantity +/- controls in the modal.
   */
  #setupQuantityControls() {
    const decreaseBtn = this.querySelector('[data-action="decrease"]');
    const increaseBtn = this.querySelector('[data-action="increase"]');
    const qtyInput = this.refs.quantityInput;

    if (!qtyInput) return;

    decreaseBtn?.addEventListener('click', () => {
      const current = parseInt(qtyInput.value, 10) || 1;
      if (current > 1) qtyInput.value = current - 1;
    });

    increaseBtn?.addEventListener('click', () => {
      const current = parseInt(qtyInput.value, 10) || 1;
      qtyInput.value = current + 1;
    });
  }

  /**
   * 全項目に回答があるか検証する。選択肢の文言はエントリーごとに違う
   * （了承しました。/ 確認しました。/ 参加する 等）ので、特定の文言とは比較しない。
   */
  #validateConsent() {
    const allAnswered = this.#items.every((item) => this.#getAnswer(item) !== '');
    const submitBtn = this.refs.submitButton;
    if (submitBtn) {
      submitBtn.disabled = !allAnswered;
    }
  }

  /**
   * Handle modal submit — add to cart via Cart API with consent line item properties.
   */
  async #handleSubmit() {
    const submitBtn = this.refs.submitButton;
    if (!submitBtn || submitBtn.disabled) return;

    const productForm = this.#getProductForm();
    if (!productForm) return;

    if (!validateVariantSelection(productForm)) {
      this.closeDialog();

      // モーダルが閉じてスクロール位置が復元された後に、
      // 未選択欄へ案内する。
      setTimeout(() => {
        validateVariantSelection(productForm);
      }, 450);

      return;
    }

    submitBtn.disabled = true;

    submitBtn.textContent = '追加中...';

    const form = this.#getProductForm()?.querySelector('form[data-type="add-to-cart-form"]');

    if (!form) {
      this.#resetSubmitButton();
      return;
    }

    // Build the payload
    const variantId = form.querySelector('input[name="id"]')?.value;
    const quantity = parseInt(this.refs.quantityInput?.value, 10) || 1;

    // Collect existing line item properties from the product form
    const properties = {};
    const formData = new FormData(form);
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('properties[') && value) {
        const propKey = key.replace('properties[', '').replace(']', '');
        properties[propKey] = value;
      }
    }

    // 了承項目を line item property に載せる（キー = 項目名 / 値 = 選択された選択肢）
    this.#items.forEach((item) => {
      const consentText = item.dataset.consentText;
      const answer = this.#getAnswer(item);
      if (consentText && answer) {
        properties[consentText] = answer;
      }
    });

    // Collect section IDs for cart drawer section rendering
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item.dataset?.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    const body = {
      id: parseInt(variantId, 10),
      quantity,
      properties,
    };

    if (sectionIds.length > 0) {
      body.sections = sectionIds.join(',');
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (data.status) {
        alert(data.description || data.message || 'エラーが発生しました');
        this.#resetSubmitButton();
        return;
      }

      // Success — show confirmation, then close and dispatch event
      const productId = this.dataset.productId;

      // カートの実件数を先に取る。ヘッダーのバッジ（cart-icon.js）は
      // CartUpdateEvent（cart:update）だけを購読しているので、CartAddEvent しか投げないと
      // 2件目以降のバッジが更新されない
      const itemCount = await this.#fetchCartItemCount();

      await this.#showSuccess();
      this.closeDialog();

      document.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'consent-modal',
          itemCount: quantity,
          productId,
          sections: data.sections,
        })
      );

      if (itemCount != null) {
        // source は 'product-form-component' 以外にする。product-form 由来だと
        // cart-icon が「現在の表示数 + itemCount」で加算してしまう（ここは実数を渡したい）
        document.dispatchEvent(
          new CartUpdateEvent({ itemCount }, 'consent-modal', {
            source: 'consent-modal',
            itemCount,
            productId,
            sections: data.sections,
          })
        );
      }

      // #267 の抑止フラグは sticky-add-to-cart 側がカート追加完了で 'false' に戻すため、
      // 次のクリックで fly-to-cart が復活する。了承モーダルがある間は常に立てておく
      this.#suppressAddToCartAnimation();
    } catch (error) {
      console.error('Consent modal add to cart error:', error);
      alert('カートへの追加に失敗しました。もう一度お試しください。');
      this.#resetSubmitButton();
    }
  }

  /**
   * カート内の実件数を返す（取得できなければ null）。
   * `/cart/add.js` のレスポンスは追加した明細だけで件数を持たないため別途取る。
   * @returns {Promise<number | null>}
   */
  async #fetchCartItemCount() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await response.json();
      return typeof cart?.item_count === 'number' ? cart.item_count : null;
    } catch (error) {
      console.error('Consent modal cart count fetch error:', error);
      return null;
    }
  }

  /**
   * 「カートに入れる」の fly-to-cart / チェックマークを抑止するフラグを立て直す。
   * 実際の投入はモーダルの送信時なので、クリック時点でアニメーションを出してはいけない。
   */
  #suppressAddToCartAnimation() {
    const button = this.#getProductForm()?.querySelector('[ref="addToCartButton"]');
    if (button instanceof HTMLElement) button.dataset.puppet = 'true';
  }

  /**
   * Show a success overlay inside the modal for 1.5 seconds.
   */
  #showSuccess() {
    return new Promise((resolve) => {
      const inner = this.querySelector('.consent-modal__inner');
      if (!inner) { resolve(); return; }

      const overlay = document.createElement('div');
      overlay.className = 'consent-modal__success';
      overlay.innerHTML = `
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <circle cx="24" cy="24" r="23" stroke="currentColor" stroke-width="2"/>
          <path d="M14 24.5L21 31.5L34 18.5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>カートに追加しました</span>
      `;
      inner.appendChild(overlay);

      setTimeout(() => {
        overlay.remove();
        resolve();
      }, 1500);
    });
  }

  #resetSubmitButton() {
    const submitBtn = this.refs.submitButton;
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.textContent = 'カートに追加する';
    }
  }

  /**
   * Show the consent modal dialog.
   */
  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    // 入力と送信ボタンをリセット（開くたびに未回答から始める）
    this.querySelectorAll('.consent-modal__select').forEach((s) => {
      s.selectedIndex = 0;
    });
    this.querySelectorAll('input[type="radio"]').forEach((r) => {
      r.checked = false;
    });
    this.querySelectorAll('.consent-modal__text-input').forEach((i) => {
      i.value = '';
    });
    this.#resetSubmitButton();
    // Re-disable since the fields were just reset
    if (this.refs.submitButton) {
      this.refs.submitButton.disabled = true;
    }

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();

      dialog.addEventListener('click', this.#handleBackdropClick);
      dialog.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Close on click outside the modal content (backdrop area).
   */
  #handleBackdropClick = (e) => {
    if (e.target === this.refs.dialog) {
      this.closeDialog();
    }
  };

  /**
   * Close on Escape key.
   */
  #handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.closeDialog();
    }
  };

  /**
   * Close the consent modal dialog with animation.
   */
  closeDialog() {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    dialog.removeEventListener('click', this.#handleBackdropClick);
    dialog.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    let resolved = false;
    const onEnd = () => {
      if (resolved) return;
      resolved = true;

      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

      dialog.close();
      dialog.classList.remove('dialog-closing');
    };

    dialog.addEventListener('animationend', onEnd, { once: true });
    setTimeout(onEnd, 400);
  }
}

if (!customElements.get('consent-modal-component')) {
  customElements.define('consent-modal-component', ConsentModalComponent);
}
